/**
 * Token estimation, context window lookup, and per-conversation analysis.
 * Composes tree-walker functions with token math to produce ConversationAnalysis.
 *
 * @module token-estimator
 */

import { walkConversationTree, filterMessages, scanConversationImages, countToolCalls, countSystemTokens } from './tree-walker.js';
import { estimatePerceivedWait, summarizePerceivedWait, TTFB_DEFAULT_S } from './perceived-wait-estimator.js';

/** @typedef {import('./types.js').ContextWindow} ContextWindow */
/** @typedef {import('./types.js').ConversationAnalysis} ConversationAnalysis */
/** @typedef {import('./types.js').RawConversation} RawConversation */

/**
 * Context window lookup table.
 * Keys are model slug prefixes; values are {nominal, effective} token counts.
 * @type {Object<string, ContextWindow>}
 */
const CONTEXT_WINDOWS = {
  'gpt-4o':          { nominal: 128_000, effective: 80_000 },
  'gpt-4o-mini':     { nominal: 128_000, effective: 80_000 },
  'gpt-5':           { nominal: 128_000, effective: 80_000 },
  'gpt-4':           { nominal: 8_000,   effective: 6_000 },
  'gpt-3.5-turbo':   { nominal: 16_000,  effective: 12_000 },
  'o1-preview':      { nominal: 128_000, effective: 80_000 },
  'o1-mini':         { nominal: 128_000, effective: 80_000 },
  'o3-mini':         { nominal: 200_000, effective: 120_000 },
  'text-davinci':    { nominal: 4_000,   effective: 3_000 },
};

/** Default for unknown models */
const DEFAULT_WINDOW = { nominal: 128_000, effective: 80_000 };

/**
 * Estimate token count for a text string.
 * Uses chars/4 heuristic — approximates GPT tokenization within ~10%.
 *
 * @param {string} text
 * @returns {number}
 */
export function estimateTokens(text) {
  if (!text) return 0;
  return Math.ceil(text.length / 4);
}

/**
 * Look up context window for a model slug.
 *
 * Matches exact slug first, then tries prefix matching (e.g. "text-davinci-003"
 * matches "text-davinci"). Unknown slugs default to 128K/80K.
 *
 * @param {string|null} modelSlug
 * @returns {ContextWindow}
 */
export function getContextWindow(modelSlug) {
  if (!modelSlug) return { ...DEFAULT_WINDOW };

  // Exact match
  if (modelSlug in CONTEXT_WINDOWS) {
    return { ...CONTEXT_WINDOWS[modelSlug] };
  }

  // Prefix match (handles "text-davinci-003", "gpt-4-gizmo", etc.)
  for (const prefix of Object.keys(CONTEXT_WINDOWS)) {
    if (modelSlug.startsWith(prefix)) {
      return { ...CONTEXT_WINDOWS[prefix] };
    }
  }

  return { ...DEFAULT_WINDOW };
}

/**
 * Normalize gizmo_type from API format to canonical names.
 * Official exports already use canonical names; API data uses internal codenames.
 *
 * @param {string|null} gizmoType
 * @returns {string|null}
 */
function normalizeGizmoType(gizmoType) {
  if (!gizmoType) return null;
  if (gizmoType === 'snorlax') return 'project';
  if (gizmoType === 'gpt') return 'custom';
  return gizmoType;
}

/**
 * Compute median of a sorted-in-place numeric array.
 * Returns null for empty arrays.
 * @param {number[]} arr - Will be sorted in place
 * @returns {number|null}
 */
function median(arr) {
  if (arr.length === 0) return null;
  arr.sort((a, b) => a - b);
  return arr[Math.floor(arr.length / 2)];
}

/**
 * Analyze a single conversation: tree walk → filter → tokens → ghost zone → bloat → timing → trends.
 *
 * @param {RawConversation} conversation
 * @returns {ConversationAnalysis}
 */
export function analyzeConversation(conversation) {
  const title = conversation.title || 'New chat';
  const conversationId = conversation.conversation_id || '';
  const createTime = conversation.create_time || 0;
  const updateTime = conversation.update_time || createTime;
  const modelSlug = conversation.default_model_slug || null;
  const gizmoId = conversation.gizmo_id || null;
  const gizmoType = normalizeGizmoType(conversation.gizmo_type || null);
  const mapping = conversation.mapping || {};
  const currentNode = conversation.current_node || null;

  // Walk tree and filter messages
  const nodes = walkConversationTree(mapping, currentNode);
  const messages = filterMessages(nodes);
  const imageData = scanConversationImages(nodes);
  const toolCallData = countToolCalls(nodes);
  const systemTokens = countSystemTokens(nodes);

  // --- Token estimation + ghost zone ---
  let totalTokens = 0;
  let ghostZoneStartIndex = -1;
  let cumulativeTokens = 0;
  const contextWindow = getContextWindow(modelSlug);
  /** @type {number[]} cumulative tokens at each message index */
  const cumulativeAtIndex = [];

  for (let i = 0; i < messages.length; i++) {
    totalTokens += messages[i].tokens;
    cumulativeTokens += messages[i].tokens;
    cumulativeAtIndex.push(cumulativeTokens);

    if (ghostZoneStartIndex === -1 && cumulativeTokens > contextWindow.effective) {
      ghostZoneStartIndex = i;
    }
  }

  // Add invisible image tokens (tool outputs, weight:0 generated images)
  // These consume context but aren't in the filtered message list.
  const visibleImageTokens = messages.reduce((s, m) => s + m.imageTokens, 0);
  const invisibleImageTokens = imageData.totalImageTokens - visibleImageTokens;
  totalTokens += invisibleImageTokens;

  // --- Adjusted tokens (content-aware) ---
  const textTokens = totalTokens - imageData.totalImageTokens;
  const toolCallTokens = Math.ceil(toolCallData.toolCallChars / 4);
  const userFacingTotal = totalTokens + toolCallTokens;
  const ghostZoneTotal = userFacingTotal + systemTokens;

  const adjustedTokens = {
    textTokens,
    imageTokens: imageData.totalImageTokens,
    toolCallTokens,
    systemTokens,
    userFacingTotal,
    ghostZoneTotal,
  };

  // --- Adjusted ghost zone (system tokens front-loaded) ---
  let adjustedGhostZoneStartIndex = -1;
  let adjustedCumulative = systemTokens; // system tokens load first
  for (let i = 0; i < messages.length; i++) {
    adjustedCumulative += messages[i].tokens;
    if (adjustedGhostZoneStartIndex === -1 && adjustedCumulative > contextWindow.effective) {
      adjustedGhostZoneStartIndex = i;
    }
  }

  const adjustedBloatScore =
    contextWindow.effective > 0 ? userFacingTotal / contextWindow.effective : 0;

  const ghostZoneMessageCount =
    ghostZoneStartIndex === -1 ? 0 : messages.length - ghostZoneStartIndex;

  const bloatScore =
    contextWindow.effective > 0 ? totalTokens / contextWindow.effective : 0;

  // --- Ghost waste (user messages after ghost zone entry) ---
  const effectiveGhostStart = adjustedGhostZoneStartIndex !== -1
    ? adjustedGhostZoneStartIndex
    : ghostZoneStartIndex;

  let ghostWastedMessages = 0;
  if (effectiveGhostStart !== -1) {
    for (let i = effectiveGhostStart; i < messages.length; i++) {
      if (messages[i].role === 'user') ghostWastedMessages++;
    }
  }

  const chatAgeDays =
    createTime > 0 && updateTime > createTime
      ? (updateTime - createTime) / 86400
      : 0;

  let userMessageCount = 0;
  let assistantMessageCount = 0;
  for (const m of messages) {
    if (m.role === 'user') userMessageCount++;
    else assistantMessageCount++;
  }

  // --- Turn pairs: user→assistant exchanges ---
  /** @type {import('./types.js').TurnPair[]} */
  const turnPairs = [];

  for (let i = 0; i < messages.length - 1; i++) {
    if (messages[i].role !== 'user' || messages[i + 1].role !== 'assistant') continue;

    const user = messages[i];
    const asst = messages[i + 1];

    const promptLength = user.text.length;
    const responseLength = asst.text.length;

    // TTFB: assistant start - user start
    let ttfb = null;
    if (user.timestamp && asst.timestamp) {
      ttfb = asst.timestamp - user.timestamp;
    }

    // Total wait: assistant end - user start
    let totalWait = null;
    if (user.timestamp && asst.updateTime) {
      totalWait = asst.updateTime - user.timestamp;
    }

    // Stream duration: assistant end - assistant start
    let streamDuration = null;
    if (asst.timestamp && asst.updateTime) {
      streamDuration = asst.updateTime - asst.timestamp;
    }

    turnPairs.push({
      userIndex: i,
      assistantIndex: i + 1,
      promptLength,
      responseLength,
      promptTokens: user.tokens,
      responseTokens: asst.tokens,
      cumulativeTokens: cumulativeAtIndex[i],
      responsePromptRatio: promptLength > 0 ? responseLength / promptLength : Infinity,
      ttfb,
      totalWait,
      streamDuration,
    });
  }

  // --- Perceived wait enrichment ---
  // Per-conversation TTFB fallback: median of actual TTFBs (positive only, filter timestamp noise)
  const actualTTFBs = turnPairs.filter(t => t.ttfb !== null && t.ttfb > 0).map(t => t.ttfb);
  const ttfbFallback = median([...actualTTFBs]) ?? TTFB_DEFAULT_S;

  for (const tp of turnPairs) {
    const asst = messages[tp.assistantIndex];
    tp.perceivedWait = estimatePerceivedWait({
      ttfb: tp.ttfb ?? ttfbFallback,
      streamDuration: tp.streamDuration,
      messagesInDOM: tp.assistantIndex,
      responseChars: asst ? asst.renderChars : tp.responseLength,
    });
  }
  const perceivedWait = summarizePerceivedWait(turnPairs);

  // --- Aggregate timing metrics ---
  const ttfbs = turnPairs.filter(t => t.ttfb !== null).map(t => /** @type {number} */ (t.ttfb));
  const totalWaits = turnPairs.filter(t => t.totalWait !== null).map(t => /** @type {number} */ (t.totalWait));
  const streamDurations = turnPairs.filter(t => t.streamDuration !== null).map(t => /** @type {number} */ (t.streamDuration));

  const medianTTFB = median([...ttfbs]);
  const medianTotalWait = median([...totalWaits]);
  const medianStreamDuration = median([...streamDurations]);

  // --- Aggregate sizing metrics ---
  const responseLengths = turnPairs.map(t => t.responseLength);
  const promptLengths = turnPairs.map(t => t.promptLength);
  const respPromptRatios = turnPairs.filter(t => t.promptLength > 0).map(t => t.responsePromptRatio);

  const medianResponseLength = median([...responseLengths]) ?? 0;
  const medianPromptLength = median([...promptLengths]) ?? 0;
  const medianResponsePromptRatio = median([...respPromptRatios]);

  // --- Trend metrics: early half vs late half ---
  let responseDragRatio = null;
  let contextDependencyGrowth = null;
  let promptLengthTrend = null;
  let responseLengthTrend = null;

  if (turnPairs.length >= 4) {
    const half = Math.floor(turnPairs.length / 2);
    const earlyPairs = turnPairs.slice(0, half);
    const latePairs = turnPairs.slice(half);

    // Response drag: late TTFB / early TTFB
    const earlyTTFBs = earlyPairs.filter(t => t.ttfb !== null).map(t => /** @type {number} */ (t.ttfb));
    const lateTTFBs = latePairs.filter(t => t.ttfb !== null).map(t => /** @type {number} */ (t.ttfb));
    const earlyMedTTFB = median([...earlyTTFBs]);
    const lateMedTTFB = median([...lateTTFBs]);
    if (earlyMedTTFB !== null && lateMedTTFB !== null && earlyMedTTFB > 0) {
      responseDragRatio = lateMedTTFB / earlyMedTTFB;
    }

    // Context dependency: late resp/prompt ratio / early ratio
    const earlyRatios = earlyPairs.filter(t => t.promptLength > 0).map(t => t.responsePromptRatio);
    const lateRatios = latePairs.filter(t => t.promptLength > 0).map(t => t.responsePromptRatio);
    const earlyMedRatio = median([...earlyRatios]);
    const lateMedRatio = median([...lateRatios]);
    if (earlyMedRatio !== null && lateMedRatio !== null && earlyMedRatio > 0) {
      contextDependencyGrowth = lateMedRatio / earlyMedRatio;
    }

    // Prompt length trend
    const earlyPrompts = earlyPairs.map(t => t.promptLength);
    const latePrompts = latePairs.map(t => t.promptLength);
    const earlyMedPrompt = median([...earlyPrompts]);
    const lateMedPrompt = median([...latePrompts]);
    if (earlyMedPrompt !== null && lateMedPrompt !== null && earlyMedPrompt > 0) {
      promptLengthTrend = lateMedPrompt / earlyMedPrompt;
    }

    // Response length trend
    const earlyResps = earlyPairs.map(t => t.responseLength);
    const lateResps = latePairs.map(t => t.responseLength);
    const earlyMedResp = median([...earlyResps]);
    const lateMedResp = median([...lateResps]);
    if (earlyMedResp !== null && lateMedResp !== null && earlyMedResp > 0) {
      responseLengthTrend = lateMedResp / earlyMedResp;
    }
  }

  // --- Aggregate content profile ---
  let aggCodeBlockCount = 0, aggCodeBlockChars = 0;
  let aggTableCount = 0, aggTableChars = 0;
  let aggTotalChars = 0, aggRenderChars = 0;

  for (const m of messages) {
    aggCodeBlockCount += m.contentProfile.codeBlockCount;
    aggCodeBlockChars += m.contentProfile.codeBlockChars;
    aggTableCount += m.contentProfile.tableCount;
    aggTableChars += m.contentProfile.tableChars;
    aggTotalChars += m.text.length;
    aggRenderChars += m.renderChars;
  }

  const contentProfile = {
    plainTextChars: aggTotalChars - aggCodeBlockChars - aggTableChars,
    codeBlockCount: aggCodeBlockCount,
    codeBlockChars: aggCodeBlockChars,
    tableCount: aggTableCount,
    tableChars: aggTableChars,
    uploadedImageCount: imageData.userImageCount,
    generatedImageCount: imageData.generatedImageCount,
    toolCallCount: toolCallData.toolCallCount,
    toolCallChars: toolCallData.toolCallChars,
    systemTokenEstimate: systemTokens,
  };

  // Determine dominant content type
  const typeCounts = {
    text: contentProfile.plainTextChars,
    code: aggCodeBlockChars,
    table: aggTableChars,
    image: imageData.imageCount * 5000,
  };
  let dominantContentType = 'text';
  let maxTypeChars = 0;
  for (const [type, chars] of Object.entries(typeCounts)) {
    if (chars > maxTypeChars) {
      maxTypeChars = chars;
      dominantContentType = type;
    }
  }

  const renderProfile = {
    adjustedRenderChars: aggRenderChars,
    dominantContentType,
    renderMultiplier: aggTotalChars > 0 ? aggRenderChars / aggTotalChars : 1,
  };

  const ghostWasteRate = userMessageCount > 0 ? ghostWastedMessages / userMessageCount : 0;

  return {
    conversationId,
    title,
    messages,
    messageCount: messages.length,
    userMessageCount,
    assistantMessageCount,
    totalTokens,
    bloatScore,
    ghostZoneStartIndex,
    ghostZoneMessageCount,
    chatAgeDays,
    modelSlug,
    contextWindow,
    gizmoId,
    gizmoType,
    createTime,
    updateTime,

    // Turn-level data
    turnPairs,
    perceivedWait,

    // Timing aggregates
    medianTTFB,
    medianTotalWait,
    medianStreamDuration,

    // Sizing aggregates
    medianResponseLength,
    medianPromptLength,
    medianResponsePromptRatio,

    // Trends
    responseDragRatio,
    contextDependencyGrowth,
    promptLengthTrend,
    responseLengthTrend,

    // Image metrics
    imageCount: imageData.imageCount,
    imageTokens: imageData.totalImageTokens,
    userImageCount: imageData.userImageCount,
    generatedImageCount: imageData.generatedImageCount,

    // Content-aware metrics
    contentProfile,
    adjustedTokens,
    adjustedBloatScore,
    adjustedGhostZoneStartIndex,
    renderProfile,
    ghostWastedMessages,
    ghostWasteRate,
  };
}
